import type { FC } from "react";
import { CustomPrintoutDeleteDialog } from "./CustomPrintoutDeleteDialog";
import { CustomPrintoutLeaveDialog } from "./CustomPrintoutLeaveDialog";
import { CustomPrintoutNameDialog } from "./CustomPrintoutNameDialog";
import { CustomPrintoutRestoreDialog } from "./CustomPrintoutRestoreDialog";
import { CustomPrintoutSaveDialog } from "./CustomPrintoutSaveDialog";
import type { CustomPrintoutSummary } from "./customPrintoutTypes";
import type { CustomPrintoutActions } from "./useCustomPrintoutActions";
import type { CustomPrintoutVersionActions } from "./useCustomPrintoutVersionView";
import type { useLeaveGuard } from "./useLeaveGuard";

type Props = {
	actions: CustomPrintoutActions;
	leaveGuard: ReturnType<typeof useLeaveGuard>;
	versions: CustomPrintoutVersionActions;
	printouts: CustomPrintoutSummary[];
	/** The shown printout's id; a rename may keep its own name. */
	printoutId: string | null;
	isGenerating: boolean;
};

/**
 * The Custom mode's dialogs: naming (Save of a new printout, Rename), saving a new version, Delete, Restore, and
 * throwing away unsaved work (by leaving it, or by Discard).
 */
export const CustomPrintoutDialogs: FC<Props> = ({ actions, leaveGuard, versions, printouts, printoutId, isGenerating }) => {
	const { nameDialog, deleteTarget } = actions;
	const { viewedVersionNumber } = versions;
	return (
		<>
			{versions.isAskingRestore && viewedVersionNumber !== null && (
				<CustomPrintoutRestoreDialog
					open
					versionNumber={viewedVersionNumber}
					isRestoring={versions.isRestoring}
					onConfirm={versions.confirmRestore}
					onClose={versions.cancelRestore}
				/>
			)}
			{actions.isConfirmingSave && <CustomPrintoutSaveDialog open isSaving={actions.isSaving} onConfirm={actions.confirmSave} onClose={actions.cancelSave} />}
			{nameDialog !== null && (
				<CustomPrintoutNameDialog
					key={nameDialog.key}
					open
					mode={nameDialog.mode}
					initialName={nameDialog.initialName}
					otherNames={printouts.filter((printout) => nameDialog.mode === "create" || printout.id !== printoutId).map((printout) => printout.name)}
					isSaving={actions.isNaming}
					onConfirm={actions.confirmName}
					onClose={actions.closeName}
				/>
			)}
			{deleteTarget !== null && (
				<CustomPrintoutDeleteDialog
					open
					name={deleteTarget.name}
					isDeleting={actions.isDeleting}
					onConfirm={actions.confirmDelete}
					onClose={actions.closeDelete}
				/>
			)}
			<CustomPrintoutLeaveDialog
				open={leaveGuard.isAsking}
				reason="leave"
				isGenerating={isGenerating}
				isNew={printoutId === null}
				onLeave={leaveGuard.leave}
				onStay={leaveGuard.stay}
			/>
			<CustomPrintoutLeaveDialog
				open={actions.isConfirmingDiscard}
				reason="discard"
				isGenerating={isGenerating}
				isNew={printoutId === null}
				onLeave={actions.confirmDiscard}
				onStay={actions.cancelDiscard}
			/>
		</>
	);
};
